import { Component, Prop } from 'vue-property-decorator';
import { Block } from '../../../helpers/components';
import { BaseBlock } from '../../base-block';
import { apartmentInfoData } from './apartment-info.data';
import { ApartmentFeature } from '@/enums/ApartmentFeature';
import { ApartmentDTO } from '../../../model/ApartmentDTO';
import { HypoCalcInfoDTO } from '../../../model/HypoCalcInfoDTO';
import { ImageResourceDTO } from '../../../model/ImageResourceDTO';
import { ParkingPlaceDTO } from '../../../model/ParkingPlaceDTO';
import { StorageFacilityDTO } from '../../../model/StorageFacilityDTO';
import { ApartmentButtonsBlock } from '../apartment-buttons/apartment-buttons';

import { sortBy } from 'lodash-es';
import ApartmentViewsBlock from '../apartment-views/apartment-views';

interface ParkingState {
	"doubleParking": boolean;
	"doubleGarage": boolean;
	"other": boolean;
}

interface HypoCalcul {
	"loanAmount": number;
	"ownFunds": number;
	"monthlyInstallment": number;
	"totalPrice": number;
}

@Block(apartmentInfoData) @Component
export class ApartmentInfoBlock extends BaseBlock {
	@Prop() apartment: ApartmentDTO;
	@Prop() hypoCalcInfo: HypoCalcInfoDTO;

	@Prop() storageFacilityImages: ImageResourceDTO[][];
	@Prop() parkingPlaceImages: ImageResourceDTO[][];

	doubleParkingState: ParkingPlaceDTO[];
	doubleGarageState: ParkingPlaceDTO[];

	parkingState: ParkingState = {
		"doubleParking": false,
		"doubleGarage": false,
		"other": false,
	}

	hypoCalcOptions = {
		"minOwnFundsPercent": 10,
		"maxOwnFundsPercent": 90,
		"minMaturity": 5,
		"maxMaturity": 30,
	}

	hypoCalcKey = 1;

	// pohledy na dum, byt patro
	planLocationLoaded: boolean = false;
	viewHouseLoaded: boolean = false;
	floorPlanFloorLoaded: boolean = false;

	expandRooms: boolean = !this.$store.isTouchDevice;
	isTouchDevice: boolean = this.$store.isTouchDevice;

	get balconies() {
		return this.apartment.balconies.filter(b => !b.glassCovered);
	}

	get balconiesGlassCovered() {
		return this.apartment.balconies.filter(b => b.glassCovered);
	}

	get terraces() {
		return this.apartment.terraces.filter(b => !b.glassCovered);
	}

	get terracesGlassCovered() {
		return this.apartment.terraces.filter(b => b.glassCovered);
	}

	get rooms() {
		return sortBy(this.apartment.rooms, r => r.order);
	}

	get additionalCostFeaturesList() {
		return (this.apartment.additionalCostFeaturesList || '').split('\n').filter(s => s.trim().length > 0);
	}

	get hasPremiumPriceOnRequest() {
		return this.apartment.features.includes(ApartmentFeature.PremiumPriceOnRequest);
	}

	get isCompleted() {
		return new Date(this.apartment.completionDate) < new Date();
	}

	getStorageFacilityLabel(storageFacility: StorageFacilityDTO) {
		return storageFacility.floorAbbr
			? `${storageFacility.floorAbbr} / ${storageFacility.abbr}`
			: storageFacility.abbr;
	}

	getParkingPlaceLabel(parkingPlace: ParkingPlaceDTO) {
		return parkingPlace.floorAbbr
			? `${parkingPlace.floorAbbr} / ${parkingPlace.abbr}`
			: parkingPlace.abbr;
	}

	get ParkingState(): ParkingState {
		this.doubleParkingState = this.apartment.parkingPlaces.filter(item => (item.typeId == 329 || item.typeId == 330));
		this.doubleGarageState = this.apartment.parkingPlaces.filter(item => (item.typeId == 326 || item.typeId == 333));

		this.parkingState.doubleParking = this.doubleParkingState.length > 0 ? true : false;
		this.parkingState.doubleGarage = this.doubleGarageState.length > 0 ? true : false;
		this.parkingState.other = (this.apartment.parkingPlaces.length - (this.doubleParkingState.length + this.doubleGarageState.length)) > 0 ? true : false;

		return this.parkingState;
	}

	get ParkingPlacesPrice(): number {
		return this.apartment.parkingPlaces.reduce((suma, pp) => suma + pp.totalPriceWithVAT, 0);
	}

	get StoragesPrice(): number {

		return this.apartment.storageFacilities.reduce((suma, pp) => suma + pp.totalPriceWithVAT, 0);
	}

	get HypoCalcul(): HypoCalcul {
		const rocniUrokovaSazba = this.hypoCalcInfo.annualInterestRate;
		const koeficientHypoteka = this.hypoCalcInfo.mortgageCoefficient;

		const celkovaCena = this.apartment.totalPriceWithVAT + this.ParkingPlacesPrice + this.StoragesPrice;
		const terminDokonceni = this.apartment.completionDate;
		const dokoncen = new Date(this.apartment.completionDate || 0) < new Date();   // intentionaly simplified for WMS3 (not using AppHelper.DatumPreklopeni etc.)

		const urokovaMira = 1 + ((rocniUrokovaSazba / 100) / 12);
		const terminDokonceniUver = new Date(terminDokonceni) || new Date();

		const diffTime = Math.abs(terminDokonceniUver.valueOf() - new Date().valueOf());
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

		const pocetDniDoDokonceni = Math.trunc(29.999 * Math.trunc((diffDays / 30) + 1));

		const rezervacniPoplatek = 50000; //po dohode s PePo nechano jako konstanta v kodu bez moznosti uzivatelsky menit
		const uverVlastniProstredky = rezervacniPoplatek + (celkovaCena * (this.hypoCalcInfo.ownFundsPercent / 100));
		//const cenaProUver = (dokoncen || (pocetDniDoDokonceni - 30) <= 0) ? celkovaCena : ((((celkovaCena - uverVlastniProstredky) * koeficientHypoteka) / 365) * (diffDays - 23)) + celkovaCena;
		const cenaProUver = celkovaCena;

		const minimalniAkontace = cenaProUver * (this.hypoCalcInfo.ownFundsPercent / 100);

		const vyseUver = cenaProUver - minimalniAkontace;
		//const minimalniSplatka = vyseUver * ((Math.pow(urokovaMira, (this.hypoCalcInfo.maturity * 12)) * (urokovaMira - 1)) / (Math.pow(urokovaMira, (this.hypoCalcInfo.maturity * 12)) - 1));
		// new calculation
		const minimalniSplatka = (vyseUver * (rocniUrokovaSazba / (12 * 100))) / (1 - Math.pow(1 + rocniUrokovaSazba / (12 * 100), (-12 * this.hypoCalcInfo.maturity)));

		const hypoCalcul = {
			loanAmount: vyseUver,
			ownFunds: minimalniAkontace,
			monthlyInstallment: minimalniSplatka,
			totalPrice: cenaProUver,
		}

		return hypoCalcul;
	}

	heightColumn(): void {
		document.getElementById("aprt-plan").addEventListener("load", () => {
			const infoHeight = document.getElementById("aprt-info").offsetHeight;
			const previews = document.getElementById("previews").offsetHeight;
			const planHeight = document.getElementById("aprt-plan").offsetHeight;

			//const maxHeight = infoHeight > previews ? infoHeight : previews;
			const maxHeight = Math.max(infoHeight, previews, (this.isTouchDevice ? 400 : 0))

			if (planHeight > maxHeight) {
				//document.getElementById("aprt-plan").style.maxHeight = `${maxHeight}px`;
			}
		})
	}
	get packageParam() {
		return this.apartment.packagePriceWithVAT ? this.formatNumber(this.apartment.packagePriceWithVAT) : this.apartment.packageInfo;
	}

	//hack kvuli prerenderovani modalu
	showHypoCalc(): void {
		this.hypoCalcKey++;
		this.$modal.show('calc-hypo');
	}

	//section buttons
	reactive: boolean = true;	// to make isFavorite reactive at least to a change to this variable

	get isPreview() {
		return this.$route.query.predbezne && this.$route.query.csh;
	}

	get isFavoriteProduct() {
		return this.$store.isFavoriteProduct
	}

	showForm(event: MouseEvent, formName: string) {
		event.preventDefault();
		this.$modal.show(formName);
	}

	isFavorite() {
		return this.$store.isFavoriteProduct = this.$cookiesService.getFavoriteProducts().includes(this.apartment.catalogNumber);
	}

	hideHypoCalcButton() {
		const hideCalc = this.getText('hide-hypo-calc-button')
		return hideCalc === "true"
	}

	toggleFavorite(catalogNumber: string, to: boolean) {
		this.$cookiesService.setFavoriteProducts(to ? this.$cookiesService.getFavoriteProducts().concat(catalogNumber) : this.$cookiesService.getFavoriteProducts().filter(p => p != catalogNumber));
		this.reactive = !this.reactive;
		this.$store.isFavoriteProduct = to
	}

	//created() {
	//	//const mql = window.matchMedia("(pointer: coarse)");

	//	this.expandRooms = !this.$store.isTouchDevice
	//	this.isTouchDevice = this.$store.isTouchDevice
	//}

	mounted() {
		this.heightColumn()
		this.isFavorite()
	}
}

export default ApartmentInfoBlock;
